import { z } from 'zod'

import { fileSideSchema } from './files'

/** How many files move at once (PROMPT.md §5.7). */
export const MAX_PARALLEL_TRANSFERS = 3

export const transferDirectionSchema = z.enum(['upload', 'download'])
export type TransferDirection = z.infer<typeof transferDirectionSchema>

export const transferStatusSchema = z.enum([
  'queued',
  'running',
  'done',
  'failed',
  'cancelled',
  /** Waiting for the user to say overwrite, rename or skip. */
  'conflict',
])
export type TransferStatus = z.infer<typeof transferStatusSchema>

export const conflictPolicySchema = z.enum(['overwrite', 'rename', 'skip'])
export type ConflictPolicy = z.infer<typeof conflictPolicySchema>

export const transferItemSchema = z.object({
  id: z.string(),
  serverId: z.string(),
  direction: transferDirectionSchema,
  sourcePath: z.string(),
  targetPath: z.string(),
  /** Shown in the queue; the file name, not the whole path. */
  name: z.string(),
  status: transferStatusSchema,
  /** Total size in bytes; 0 until it is known. */
  size: z.number().nonnegative(),
  transferred: z.number().nonnegative(),
  /** Bytes per second over the last sample window, or null before the first sample. */
  bytesPerSecond: z.number().nonnegative().nullable(),
  startedAt: z.iso.datetime().nullable(),
  finishedAt: z.iso.datetime().nullable(),
  /** German message when the transfer failed. */
  error: z.string().nullable(),
})
export type TransferItem = z.infer<typeof transferItemSchema>

export const enqueueTransferSchema = z.object({
  serverId: z.string().min(1),
  /** `upload` moves from the backend's filesystem to the server, `download` the other way. */
  direction: transferDirectionSchema,
  /** Files and directories; directories are expanded recursively by the backend. */
  sourcePaths: z.array(z.string().min(1)).min(1, 'Es wurde nichts ausgewählt.'),
  /** Directory the items land in. */
  targetDirectory: z.string().min(1),
  /** Applied without asking when set; otherwise a conflict stops the item and asks. */
  conflictPolicy: conflictPolicySchema.optional(),
})

export const resolveConflictSchema = z.object({
  transferId: z.string().min(1),
  policy: conflictPolicySchema,
  /** Apply the same answer to every later conflict in this queue. */
  applyToAll: z.boolean().default(false),
})

export const transferListSchema = z.object({
  items: z.array(transferItemSchema),
})

export { fileSideSchema }

export type EnqueueTransferInput = z.input<typeof enqueueTransferSchema>
export type TransferList = z.infer<typeof transferListSchema>
